import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import AdatisLogo from "../Common/AdatisLogo";

export type AuthSplitLayoutProps = {
  title: string;
  subtitle?: ReactNode;
  /** Top-right action in the form panel (e.g. Entrar / Criar conta). */
  headerAction?: ReactNode;
  /** Optional block below the subtitle (steps, plan badge, alerts). */
  lead?: ReactNode;
  children: ReactNode;
};

/**
 * Split-screen auth shell for tenant public flows (/login, /register, /forgot-password).
 * Super Admin and other ParticlesAuth screens stay unchanged.
 */
const AuthSplitLayout = ({
  title,
  subtitle,
  headerAction,
  lead,
  children,
}: AuthSplitLayoutProps) => {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  const tagline = t("auth.split.tagline");

  return (
    <div className="adatis-auth-split" data-bs-theme="light">
      <aside
        className="adatis-auth-split__brand"
        aria-label={t("auth.split.brandLabel")}
      >
        <div className="adatis-auth-split__brand-inner">
          <Link to="/" className="adatis-auth-split__logo" aria-label="Adatis">
            <AdatisLogo variant="light" linkTo={false} height={42} />
          </Link>
          <p className="adatis-auth-split__tagline">{tagline}</p>
        </div>
        <p className="adatis-auth-split__brand-footer mb-0">
          {t("footer.copyright", { year })}
        </p>
      </aside>

      <main className="adatis-auth-split__panel">
        <div className="adatis-auth-split__mobile-brand">
          <Link to="/" className="adatis-auth-split__logo" aria-label="Adatis">
            <AdatisLogo variant="light" linkTo={false} height={32} />
          </Link>
          <p className="adatis-auth-split__tagline mb-0">{tagline}</p>
        </div>

        <div className="adatis-auth-split__panel-inner">
          <div className="adatis-auth-split__header">
            <div className="adatis-auth-split__titles">
              <h1 className="adatis-auth-split__title">{title}</h1>
              {subtitle ? (
                <p className="adatis-auth-split__subtitle">{subtitle}</p>
              ) : null}
            </div>
            {headerAction ? (
              <div className="adatis-auth-split__header-action">
                {headerAction}
              </div>
            ) : null}
          </div>

          {lead ? <div className="adatis-auth-split__lead">{lead}</div> : null}

          <div className="adatis-auth-split__body">{children}</div>
        </div>
      </main>
    </div>
  );
};

export default AuthSplitLayout;
